// Self-serve customer setup page.
//   • Shows device_key + activation_code (read-only, copyable).
//   • Shows trial countdown.
//   • Lists current playlists, with delete.
//   • "Add playlist" form — Xtream (host/user/pass) OR M3U URL.
// Route: /welcome
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Trash2, Plus, CheckCircle2, Globe, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WelcomePage() {
  const navigate = useNavigate();
  const [device, setDevice]       = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [mode, setMode]           = useState("xtream"); // xtream | m3u
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({
    name: "My playlist",
    xtream_url: "",
    xtream_username: "",
    xtream_password: "",
    m3u_url: "",
  });
  const registered = useRef(false);

  // 1) Ensure we have a device_key
  const ensureDevice = useCallback(async () => {
    let dk = localStorage.getItem("deviceKey") || localStorage.getItem("device_key");
    if (!dk && !registered.current) {
      registered.current = true;
      try {
        const { data } = await axios.post(`${API}/device/register`, {
          device_name: navigator.userAgent.slice(0, 120),
          device_type: "web",
        });
        dk = data.device_key;
        localStorage.setItem("deviceKey", dk);
        localStorage.setItem("device_key", dk);
      } catch (e) {
        toast.error("Couldn't register this device. Please try again.");
        return null;
      }
    }
    return dk;
  }, []);

  const reload = useCallback(async () => {
    const dk = await ensureDevice();
    if (!dk) return;
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/device/status/${dk}`),
        axios.get(`${API}/playlist/${dk}`),
      ]);
      setDevice(s.data);
      setPlaylists(Array.isArray(p.data) ? p.data : []);
    } catch (e) {
      toast.error("Couldn't load device info.");
    }
  }, [ensureDevice]);

  useEffect(() => { reload(); }, [reload]);

  const daysLeft = useMemo(() => {
    if (!device?.trial_end) return null;
    try {
      const end = new Date(device.trial_end).getTime();
      const now = Date.now();
      if (now > end) return 0;
      return Math.ceil((end - now) / 86_400_000);
    } catch { return null; }
  }, [device]);

  const expired  = device?.status === "expired";
  const isActive = device?.is_paid || device?.status === "active" || device?.status === "trial";

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  const save = async () => {
    const dk = device?.device_key;
    if (!dk) return;
    setSaving(true);
    try {
      const payload = { device_key: dk, name: form.name, playlist_type: mode };
      if (mode === "xtream") {
        Object.assign(payload, {
          xtream_url: form.xtream_url.trim(),
          xtream_username: form.xtream_username.trim(),
          xtream_password: form.xtream_password,
        });
      } else {
        payload.m3u_url = form.m3u_url.trim();
      }
      await axios.post(`${API}/customer/playlist`, payload);
      toast.success("Playlist added");
      setForm({ name: "My playlist", xtream_url: "", xtream_username: "", xtream_password: "", m3u_url: "" });
      await reload();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const dk = device?.device_key;
    if (!dk) return;
    try {
      await axios.delete(`${API}/customer/playlist/${id}`, { params: { device_key: dk } });
      toast.success("Playlist removed");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!device) {
    return <div className="min-h-screen grid place-items-center text-white">Loading…</div>;
  }

  return (
    <div data-testid="welcome-page" className="min-h-screen bg-[#050507] text-white px-4 py-10">
      <div className="max-w-2xl mx-auto space-y-6">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold text-cyan-400">Activate NetTV</h1>
          <p className="text-white/70 text-sm">
            Your device has a <strong>free 7-day trial</strong>. Add a playlist
            below to start watching.
          </p>
        </header>

        {/* Device card */}
        <section className="rounded-xl border border-white/10 bg-black/40 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-white/50 uppercase tracking-wider">Device key</div>
              <div className="text-2xl font-mono font-semibold" data-testid="device-key">
                {device.device_key}
              </div>
            </div>
            <button
              data-testid="copy-device-key"
              onClick={() => copy(device.device_key, "Device key")}
              className="p-2 rounded hover:bg-white/10"
              title="Copy"
            ><Copy className="w-4 h-4" /></button>
          </div>

          {device.activation_code && (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-white/50 uppercase tracking-wider">Activation code</div>
                <div className="text-lg font-mono" data-testid="activation-code">{device.activation_code}</div>
              </div>
              <button
                onClick={() => copy(device.activation_code, "Activation code")}
                className="p-2 rounded hover:bg-white/10"
                title="Copy"
              ><Copy className="w-4 h-4" /></button>
            </div>
          )}

          {/* Status pill */}
          <div className="flex items-center gap-3 pt-1">
            <span
              data-testid="trial-status"
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                expired
                  ? "bg-red-500/20 text-red-300 border border-red-500/40"
                  : device.is_paid
                  ? "bg-green-500/20 text-green-300 border border-green-500/40"
                  : "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"
              }`}
            >
              <Clock className="w-3 h-3" />
              {expired
                ? "Trial expired"
                : device.is_paid
                ? "Active subscription"
                : daysLeft === 0
                ? "Last day of trial"
                : `${daysLeft} day${daysLeft === 1 ? "" : "s"} of trial left`}
            </span>
          </div>
        </section>

        {/* Playlists list */}
        <section className="rounded-xl border border-white/10 bg-black/40 p-5 space-y-3">
          <h2 className="text-lg font-medium text-cyan-400">Your playlists</h2>
          {playlists.length === 0 ? (
            <p className="text-white/60 text-sm">
              You haven't added a playlist yet. Paste yours below.
            </p>
          ) : (
            <ul className="divide-y divide-white/10" data-testid="playlist-list">
              {playlists.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-2">
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-white/50">
                      {p.playlist_type === "xtream"
                        ? `Xtream · ${p.xtream_url}`
                        : `M3U · ${(p.m3u_url || "").slice(0, 60)}${(p.m3u_url || "").length > 60 ? "…" : ""}`}
                    </div>
                  </div>
                  <button
                    data-testid={`playlist-delete-${p.id}`}
                    onClick={() => remove(p.id)}
                    className="p-2 rounded hover:bg-red-500/20 text-red-400"
                    title="Remove"
                  ><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Add playlist */}
        {!expired && (
          <section className="rounded-xl border border-white/10 bg-black/40 p-5 space-y-4">
            <h2 className="text-lg font-medium text-cyan-400">Add a playlist</h2>
            <div className="flex gap-2">
              <button
                data-testid="mode-xtream"
                onClick={() => setMode("xtream")}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  mode === "xtream" ? "bg-cyan-500 text-black" : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >Xtream Codes</button>
              <button
                data-testid="mode-m3u"
                onClick={() => setMode("m3u")}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  mode === "m3u" ? "bg-cyan-500 text-black" : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >M3U URL</button>
            </div>

            <label className="block space-y-1">
              <span className="text-xs text-white/60">Name</span>
              <input
                data-testid="form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded text-white placeholder:text-white/30"
                placeholder="My playlist"
              />
            </label>

            {mode === "xtream" ? (
              <>
                <label className="block space-y-1">
                  <span className="text-xs text-white/60">Server (host:port)</span>
                  <input
                    data-testid="form-xtream-url"
                    value={form.xtream_url}
                    onChange={(e) => setForm({ ...form, xtream_url: e.target.value })}
                    placeholder="http://portal.example.com:25461"
                    className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded text-white placeholder:text-white/30 font-mono text-sm"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block space-y-1">
                    <span className="text-xs text-white/60">Username</span>
                    <input
                      data-testid="form-xtream-user"
                      value={form.xtream_username}
                      onChange={(e) => setForm({ ...form, xtream_username: e.target.value })}
                      className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded text-white font-mono text-sm"
                      autoComplete="off"
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-xs text-white/60">Password</span>
                    <input
                      data-testid="form-xtream-pass"
                      type="password"
                      value={form.xtream_password}
                      onChange={(e) => setForm({ ...form, xtream_password: e.target.value })}
                      className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded text-white font-mono text-sm"
                      autoComplete="new-password"
                    />
                  </label>
                </div>
              </>
            ) : (
              <label className="block space-y-1">
                <span className="text-xs text-white/60">M3U / M3U8 URL</span>
                <input
                  data-testid="form-m3u-url"
                  value={form.m3u_url}
                  onChange={(e) => setForm({ ...form, m3u_url: e.target.value })}
                  placeholder="https://example.com/playlist.m3u"
                  className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded text-white placeholder:text-white/30 font-mono text-sm"
                />
              </label>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button
                data-testid="save-playlist"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-cyan-500 text-black font-semibold hover:bg-cyan-400 disabled:opacity-50"
              >
                {saving ? "Verifying…" : <><Plus className="w-4 h-4" /> Add playlist</>}
              </button>
              <span className="text-xs text-white/50">Credentials verified before saving</span>
            </div>
          </section>
        )}

        {/* Action row */}
        <div className="flex gap-3 pt-2">
          {isActive && !expired && playlists.length > 0 && (
            <button
              data-testid="go-to-menu"
              onClick={() => navigate("/menu")}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-green-500 text-black font-semibold hover:bg-green-400"
            >
              <CheckCircle2 className="w-4 h-4" /> Start watching
            </button>
          )}
          <button
            onClick={() => navigate("/")}
            className="px-5 py-3 rounded-lg bg-white/10 text-white hover:bg-white/20 text-sm"
          >
            <Globe className="w-4 h-4 inline mr-1" /> Back to home
          </button>
        </div>
      </div>
    </div>
  );
}
